import React, {MutableRefObject, useEffect, useRef, useState} from "react";
import {connect, useDispatch, useSelector} from "react-redux";
import {RouteComponentProps, withRouter} from "react-router";
import {css, SerializedStyles, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {bindActionCreators, Dispatch} from "redux";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {Drawer} from "@pg-design/drawer-module";
import {Hidden} from "@pg-design/grid-module";
import {calculateRemSize, display, flex, flexDirection, h100, mh, mr, onDesktop, p, pb, pt, pv} from "@pg-design/helpers-css";
import {FiltersIcon} from "@pg-design/icons-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {FormActions} from "@pg-mono/form";
import {appendQueryString, parseSearch} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {rpFeatureFlagNewUserFlow} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {getOfferListSubFilterParsed} from "../../offer/helpers/friendly_offer_list/get_offer_list_sub_filter_parsed";
import {resetPrivilegedOfferList} from "../../offer/list/actions/fetch_privileged_offer_list";
import {ISelectedRegion} from "../../offer/list/actions/offer_list_selected_data_actions";
import {NotificationVariant, OfferListNotificationsSubscriptionModal} from "../../offer/list/components/OfferListNotificationsSubscriptionModal";
import {OfferListSortMobile} from "../../offer/list/components/OfferListsSortMobile";
import {SearchSortString} from "../../offer/list/constants/SearchSort";
import {useDefaultDistance} from "../../offer/list/hooks/use_default_distance";
import {useOfferListingAbroadView} from "../../offer/list/hooks/use_offer_listing_abroad_view_check";
import {useMapRegionDistrictsAndSurroundings} from "../../offer/map/hooks/use_map_region_districts_and_surroundings";
import {validateSelectedRegionsWithAvailableDistrictsAndSurroundings} from "../../offer/map/utils/districts_and_surroundings/validate_selected_regions_with_available_districts_and_surroundings";
import {debouncedSearchTypingHit} from "../../tracking/algolytics_hits/search_typing_hit";
import {ILatestQuery} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {
    fetchAllSearchLists,
    IFetchAllSearchListsParams,
    resetFetchAllSearchLists,
    SearchTab,
    stopFetchAllSearchLists
} from "../actions/fetch_search_all_action";
import {resetPlaceList} from "../actions/fetch_search_places_action";
import {resetMultiRegionSearchForm, resetSearchForm, searchFormActions} from "../actions/search_form_actions";
import {resetActiveDropdownItem} from "../actions/set_active_dropdown_item";
import {setPreviousDistance} from "../actions/set_travel_time";
import {GuideTourStep} from "../constants/GuideTourStep";
import {searchFormFields} from "../constants/search_form_constants";
import {SEARCH_FORM_FOCUS_HASH} from "../constants/search_form_focus_hash";
import {searchFormWithDrawerHeight} from "../constants/search_form_with_drawer_height";
import {useCountSearchFilters} from "../hooks/use_count_search_filters";
import useFilter from "../hooks/use_filter";
import useMobileFilters from "../hooks/use_mobile_filters";
import {useRegionSearchTabs} from "../hooks/use_region_search_tabs";
import useSearchAndFilterUpdate from "../hooks/use_search_and_filter_update";
import {useSearchForm} from "../hooks/use_search_form";
import {useSearchFormSubmit} from "../hooks/use_search_form_submit";
import {useSearchGuideTour} from "../hooks/use_search_guide_tour";
import {ISearchFiltersFormValues} from "../types/ISearchFiltersFormValues";
import {RegionSearchTabsType} from "../types/RegionSearchTabsType";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {getSearchFiltersByOfferType} from "../utils/get_search_filters_by_offer_type";
import {googlePlacesDivId} from "../utils/search_constants";
import {DesktopBlend} from "./atoms/atoms";
import {ISearchAutocompleteFormValues} from "./search_autocomplete/SearchAutocomplete";
import {SlotOrderType} from "./search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchAutocompleteWithDropdown} from "./search_autocomplete/SearchAutocompleteWithDropdown";
import {SearchAutocompleteWithRegionAdjustments} from "./search_autocomplete/SearchAutocompleteWithRegionAdjustments";
import {ListingChangingButtons} from "./ListingChangingButtons";
import {MobileSearchFilters} from "./MobileSearchFilters";
import {MobileSearchTriggers} from "./MobileSearchTriggers";
import {MobileTopSearchTriggers} from "./MobileTopSearchTriggers";
import {SearchFiltersWithAccordion} from "./SearchFiltersWithAccordion";

interface IStateProps {
    formValues: SearchFormValuesType;
}
// TODO: rebrand
/* eslint-disable @typescript-eslint/no-explicit-any */
interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, any>>;
    resetSearchForm: any;
    resetMultiRegionSearchForm: any;
    resetActiveDropdownItem: typeof resetActiveDropdownItem;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
    stopFetchAllSearchLists: typeof stopFetchAllSearchLists;
    resetFetchAllSearchLists: typeof resetFetchAllSearchLists;
    resetPrivilegedOfferList: typeof resetPrivilegedOfferList;
    setPreviousDistance: typeof setPreviousDistance;
}

interface IOwnProps extends RouteComponentProps<{}> {
    onAfterSubmit?: () => void;
    latestQuery: ILatestQuery;
    selectedRegions?: ISelectedRegion[];
    multiRegionLink: () => string;
    listFriendlyLink: (params: {friendlySlug: string; offerListSubFilterSlug?: string}) => string;
    abroadListFriendlyLink: (params: {friendlySlug: string; country: string; abroadRegionSlug?: string}) => string;
    blendStyles?: SerializedStyles;
    sortActive?: boolean;
    shouldRenderListBackBtn?: boolean;
    blendTop?: string;
    blendHeightDecrease?: string;
    className?: string;
    slotOrder?: SlotOrderType;
    hideListChangingButtons?: boolean;
    isMap?: boolean;
    showTopFilters?: boolean;
    hideBottomPadding?: boolean;
    disableLocationSearch?: boolean;
    autoFocusOnInit?: boolean;
    disableFilterAutoShowcase?: boolean;
    guideTourName?: string;
}

interface IProps extends IOwnProps, IStateProps, IActionsProps {}

const SearchFormWithDrawerC = (props: IProps) => {
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const {
        isLocationSearchOpen: isLocationSearchOpenGlobal,
        setLocationSearchOpenStatus,
        toggleLocationSearch,
        addLocationSearchStatusWatcher
    } = useSearchForm();
    const isLocationSearchOpen = props.disableLocationSearch ? false : isLocationSearchOpenGlobal;

    // TODO: setDesktopTypeBlendActive & setDesktopSearchBlendActive need to be refactored,
    // probably display should depend on isLocationSearchOpen and css media query
    const [isDesktopTypeBlendActive, setDesktopTypeBlendActive] = useState(false);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);
    const viewType = useSelector((state: IRPStore) => state.viewType);
    const searchAutocompleteWrapperRef = useRef<HTMLDivElement>(null);
    const {guideTourCurrentStepId, completeGuideTourStep} = useSearchGuideTour({name: props.guideTourName});

    const {activeRegionSearchTab, regionSearchTabs, regionSearchTabChangeHandler} = useRegionSearchTabs({
        regionQuery: props.formValues?.search?.label,
        onSearchTabChange: (tab: RegionSearchTabsType) => {
            if (tab === RegionSearchTabsType.TRAVEL_TIME) {
                props.setPreviousDistance(props.latestQuery.distance);
            }
        }
    });

    const {data: mapDistrictsAndSurroundsData, isLoading: areMapDistrictsAndSurroundingsLoading} = useMapRegionDistrictsAndSurroundings({
        selectedRegions: props.selectedRegions
    });

    const scrollIntoSearchAutocompleteWrapper = () => {
        if (searchAutocompleteWrapperRef.current) {
            const marginOffset = 70;
            const elementTop = searchAutocompleteWrapperRef.current.getBoundingClientRect().top;
            const offsetTop = document.documentElement.scrollTop;
            const scrollToPosition = elementTop + offsetTop - marginOffset;

            window.scrollTo({
                top: scrollToPosition,
                behavior: "smooth"
            });
        }
    };

    useEffect(() => {
        if (!rpFeatureFlagNewUserFlow) {
            return;
        }

        if (!props.autoFocusOnInit || guideTourCurrentStepId !== GuideTourStep.SELECT_DISTRICTS || areMapDistrictsAndSurroundingsLoading) {
            return;
        }

        // should be opening and scroll into view shortly after the component is mounted
        if (validateSelectedRegionsWithAvailableDistrictsAndSurroundings(props.selectedRegions || [], mapDistrictsAndSurroundsData?.results)) {
            setTimeout(() => {
                setLocationSearchOpenStatus(true);
                addLocationSearchStatusWatcher(() => {
                    completeGuideTourStep();
                }, false);
            }, 2000);
        } else {
            completeGuideTourStep();
        }
    }, [guideTourCurrentStepId, areMapDistrictsAndSurroundingsLoading]);

    const {isMobileFiltersActive, closeMobileFilterModal, toggleMobileFilterModal, prevFilterState} = useMobileFilters({
        ...props,
        delayAutoOpen: rpFeatureFlagNewUserFlow ? guideTourCurrentStepId !== GuideTourStep.SELECT_FILTERS : true,
        onClose: () => {
            if (rpFeatureFlagNewUserFlow) {
                completeGuideTourStep();
            }
        }
    });
    const isAbroadOffersView = useOfferListingAbroadView();

    const [windowHeight, setWindowHeight] = useState(0);
    const dispatch = useDispatch();
    const {isMobile} = useUserDevice();

    useEffect(() => {
        if (!isMobile) {
            setDesktopSearchBlendActive(isLocationSearchOpen);

            if (props.autoFocusOnInit && isLocationSearchOpen) {
                scrollIntoSearchAutocompleteWrapper();
            }
        }
    }, [isMobile, isLocationSearchOpen]);

    // handle initial focus on search
    const focusOnSearch = (ref: MutableRefObject<HTMLElement | null>, callback?: () => void) => {
        if (props.location.hash === SEARCH_FORM_FOCUS_HASH && ref.current?.offsetParent) {
            props.history.replace({hash: ""});
            setLocationSearchOpenStatus(true);
            callback && callback();
        }
    };
    // focus on search
    const onInputMount = (ref: MutableRefObject<HTMLInputElement | null>) => {
        focusOnSearch(ref, () =>
            setTimeout(() => {
                ref.current?.focus();
            }, 0)
        );
    };
    // and mobile focus on search
    const onMobileSearchTriggerMount = (ref: MutableRefObject<HTMLDivElement | null>) => {
        focusOnSearch(ref);
    };

    useEffect(() => {
        const query = props.latestQuery as Record<string, string>;
        (async () => {
            if (props.location.pathname === props.multiRegionLink()) {
                await props.resetMultiRegionSearchForm(query, location.search);
            } else {
                await props.resetSearchForm(query);
            }
        })();
    }, []);

    useEffect(() => {
        setWindowHeight(document.body.clientHeight);
    }, [isDesktopSearchBlendActive]);

    /**
     * Callbacks
     */
    const onAfterTabSwitch = () => {
        props.resetActiveDropdownItem();
    };

    /**
     * Submit logic
     */
    const onSubmit = useSearchFormSubmit({
        latestQuery: props.latestQuery,
        listFriendlyLink: ({friendlySlug, offerListSubFilterSlug}) => {
            if (viewType.current === ViewType.OFFER_LIST_MAP && offerListSubFilterSlug) {
                const subFilterQuery = getOfferListSubFilterParsed(offerListSubFilterSlug);
                return appendQueryString(props.listFriendlyLink({friendlySlug, offerListSubFilterSlug}), subFilterQuery);
            }

            return props.listFriendlyLink({friendlySlug, offerListSubFilterSlug});
        },
        abroadListFriendlyLink: (params) => {
            return props.abroadListFriendlyLink && props.abroadListFriendlyLink(params);
        },
        multiRegionLink: props.multiRegionLink,
        onAfterSubmit: props.onAfterSubmit
    });

    const onChange = (values: Partial<ISearchAutocompleteFormValues>) => {
        if (!!values.search?.label && props.formValues.search.label !== values.search.label) {
            debouncedSearchTypingHit({query: values.search.label});
        }

        props.formActions.update(values);
    };
    const onSearchSubmit = () => {
        onSubmit(props.formValues);
    };

    const {onAfterChange} = useSearchAndFilterUpdate({
        resetPrivilegedOfferList: resetPrivilegedOfferList,
        formValues: props.formValues,
        latestQuery: props.latestQuery,
        onSubmit: (formValues: SearchFormValuesType) => onSubmit(formValues)
    });

    const {isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters} = useFilter({
        formValues: props.formValues,
        closeMobileFilterModal: closeMobileFilterModal,
        onAfterChange: onAfterChange,
        onSubmit: (formValues: SearchFormValuesType) => onSubmit(formValues),
        enableFiltersAutoOpen: rpFeatureFlagNewUserFlow ? guideTourCurrentStepId === GuideTourStep.SELECT_FILTERS : false,
        disableAutoShowcase: props.disableFilterAutoShowcase,
        onClose: () => {
            if (rpFeatureFlagNewUserFlow) {
                completeGuideTourStep();
            }
        }
    });

    /**
     * Handle filters
     */
    const searchFiltersConfig = getSearchFiltersByOfferType(props.formValues.type?.[0]);
    const {totalCount: numberOfSearchFilters, countBySlots} = useCountSearchFilters<ISearchFiltersFormValues>(props.formValues, searchFiltersConfig, {
        afterMountOnly: true
    });

    const discardFiltersModal = () => {
        closeMobileFilterModal();
        if (prevFilterState.current != null) {
            props.formActions.update(prevFilterState.current);
        }
    };

    const getUrl = (isMap: boolean) => {
        const search = parseSearch(props.location.search);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {page, sort, ...validSearch} = search;
        const {friendlySlug} = props.match.params as {friendlySlug: string | undefined};
        if (friendlySlug) {
            return appendQueryString(isMap ? rpAppLink.offer.mapFriendly({friendlySlug}) : rpAppLink.offer.listFriendly({friendlySlug}), validSearch);
        }
        return appendQueryString(isMap ? rpAppLink.offer.multiListMap() : rpAppLink.offer.multiList(), validSearch);
    };

    const onTravelTimeCancel = () => {
        regionSearchTabChangeHandler(RegionSearchTabsType.LOCATION);
        dispatch(resetPlaceList());
    };

    const renderSearchAutocomplete = (onDropdownStatusChange: (isOpen: boolean) => void, isInModal?: boolean) => {
        if (props.disableLocationSearch) {
            return null;
        }

        return isAbroadOffersView ? (
            <SearchAutocompleteWithDropdown
                isDropdownOpen={isLocationSearchOpen}
                onDropdownStatusChange={setLocationSearchOpenStatus}
                formProps={props}
                onAfterChange={onAfterChange}
                onSearchSubmit={onSearchSubmit}
                onAfterTabSwitch={() => null}
                onInputMount={() => null}
                includeDistanceFilter
                isInModal={isInModal}
                placeholder="Zmień lokalizację"
                showArrowOnMobile
            />
        ) : (
            <SearchAutocompleteWithRegionAdjustments
                ref={searchAutocompleteWrapperRef}
                activeRegionSearchTab={activeRegionSearchTab}
                defaultDistanceValue={defaultDistanceValue}
                fetchAllSearchLists={props.fetchAllSearchLists}
                formActions={props.formActions}
                formValues={props.formValues}
                isInModal={isInModal}
                onAfterChange={onAfterChange}
                onAfterTabSwitch={onAfterTabSwitch}
                onChange={onChange}
                isDropdownOpen={isLocationSearchOpen}
                onDropdownStatusChange={onDropdownStatusChange}
                onInputMount={onInputMount}
                onSearchSubmit={onSearchSubmit}
                onTravelTimeCancel={onTravelTimeCancel}
                setDesktopTypeBlendActive={setDesktopTypeBlendActive}
                slotOrder={props.slotOrder}
                regionSearchTabChangeHandler={regionSearchTabChangeHandler}
                regionSearchTabs={regionSearchTabs}
            />
        );
    };

    /**
     * Render
     */
    const defaultDistanceValue = useDefaultDistance(props.formValues.distance || props.latestQuery?.distance);

    const mobileFiltersProps = {
        selectedRegionsCount: props.formValues["search"].regions ? props.formValues["search"].regions.length : 0,
        selectedFiltersCount: numberOfSearchFilters,
        onToggleSearchClick: toggleLocationSearch,
        onToggleFiltersClick: toggleMobileFilterModal,
        onToggleSortClick: props.sortActive ? () => setMobileSortActive((prev) => !prev) : null,
        mapUrl: props.shouldRenderListBackBtn ? getUrl(true) : undefined,
        listUrl: !props.shouldRenderListBackBtn ? getUrl(false) : undefined,
        latestQuery: props.latestQuery,
        onMount: onMobileSearchTriggerMount,
        firstSelectedRegion: props.formValues.search?.regions?.[0]?.name,
        sortActive: props.sortActive
    };

    const onDrawerClose = () => {
        if (props.latestQuery.price_1 !== props.formValues.price.upper) {
            props.formActions.update({price: {lower: "", upper: props.latestQuery.price_1 || ""}});
        }
        closeDrawer();
    };

    return (
        <Wrapper className={props.className}>
            <DesktopBlend
                isActive={isDesktopSearchBlendActive || isDesktopTypeBlendActive}
                pageHeight={`${windowHeight}px`}
                top={props.blendTop || `${searchFormWithDrawerHeight}px`}
                heightDecrease={props.blendHeightDecrease ? props.blendHeightDecrease : `${searchFormWithDrawerHeight}px`}
            />
            {!props.hideListChangingButtons && (
                <Hidden on="desktop" css={pv(1.5)}>
                    <ListingChangingButtons isMap={props.isMap} />
                </Hidden>
            )}

            {!isMobile && (
                <div css={[searchSectionWrapper]}>
                    {!props.hideListChangingButtons && (
                        <Hidden on="mobile" css={changingButtonsWrapper}>
                            <ListingChangingButtons isMap={props.isMap} />
                        </Hidden>
                    )}
                    <div css={searchFormWrapperStyle}>
                        <div css={searchInputWrapper}>
                            <div id={googlePlacesDivId} />
                            {renderSearchAutocomplete(setLocationSearchOpenStatus)}
                        </div>
                    </div>
                    <div css={filtersButtonsStyle}>
                        <Button
                            size="small"
                            variant="filled_primary"
                            iconLeft={FiltersIcon}
                            css={[actionButtonStyle, display("none"), onDesktop(display("inline-flex"))]}
                            onClick={() => setDrawerOpen(true)}
                        >
                            FILTRY
                            {numberOfSearchFilters > 0 ? (
                                <Badge variant="label_danger" css={filtersCountBadge}>
                                    {numberOfSearchFilters}
                                </Badge>
                            ) : null}
                        </Button>
                        {props.formValues.search?.regions && <OfferListNotificationsSubscriptionModal variant={NotificationVariant.ICON} />}
                    </div>
                </div>
            )}
            <Drawer isOpen={isDrawerOpen} onDrawerClose={onDrawerClose} contentStyle={{backgroundColor: getThemeVariable("colors.background.100")}}>
                <Drawer.Header>Filtry</Drawer.Header>
                <Drawer.Content css={[drawerContentStyle, h100]}>
                    <SearchFiltersWithAccordion
                        searchFiltersConfig={searchFiltersConfig}
                        searchFiltersCountBySlots={countBySlots}
                        values={props.formValues}
                        onAfterChange={onAfterChange}
                        onChange={props.formActions.update}
                        onSubmit={onSearchSubmit}
                        onClose={onDrawerClose}
                        onResetForm={resetFilters}
                    />
                </Drawer.Content>
            </Drawer>

            {isMobile && (
                <>
                    <MobileOptionsModal
                        modalHeading="Wyszukiwanie"
                        isOpen={isLocationSearchOpen}
                        onModalClose={() => {
                            setLocationSearchOpenStatus(false);
                        }}
                    >
                        <div id={googlePlacesDivId} />
                        {renderSearchAutocomplete(setLocationSearchOpenStatus, true)}
                    </MobileOptionsModal>
                    <MobileOptionsModal
                        modalHeading="Filtry"
                        isOpen={isMobileFiltersActive}
                        onModalClose={discardFiltersModal}
                        css={[flex(), flexDirection("column")]}
                    >
                        <MobileSearchFilters
                            searchFiltersConfig={searchFiltersConfig}
                            searchFiltersCountBySlots={countBySlots}
                            values={props.formValues}
                            onAfterChange={onAfterChange}
                            onChange={props.formActions.update}
                            onSubmit={onSearchSubmit}
                            onMobileCloseModal={closeMobileFilterModal}
                            onMobileResetForm={resetFilters}
                            isInModal
                        />
                    </MobileOptionsModal>
                    {props.sortActive && (
                        <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={() => setMobileSortActive(false)}>
                            <OfferListSortMobile
                                name="mobile-sort"
                                onSubmit={() => setMobileSortActive(false)}
                                latestQuerySort={props.latestQuery.sort as SearchSortString}
                                onMobileResetForm={resetFilters}
                            />
                        </MobileOptionsModal>
                    )}
                    {props.showTopFilters ? (
                        <MobileTopSearchTriggers
                            searchFieldPropsValue={props.formValues.search}
                            {...mobileFiltersProps}
                            isMap={props.isMap}
                            showLocationSearch
                        />
                    ) : (
                        <MobileSearchTriggers {...mobileFiltersProps} positionFixed hideBottomPadding={props.hideBottomPadding} />
                    )}
                </>
            )}
        </Wrapper>
    );
};

export const SearchFormWithDrawer = withRouter(connect(mapStateToProps, mapActionsToProps)(SearchFormWithDrawerC));

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.search.formValues
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators(
            {
                resetSearchForm,
                resetActiveDropdownItem,
                resetMultiRegionSearchForm,
                fetchAllSearchLists,
                stopFetchAllSearchLists,
                resetFetchAllSearchLists,
                resetPrivilegedOfferList,
                setPreviousDistance
            },
            dispatch
        )
    };
}

const searchSectionWrapper = css`
    ${display("none")};

    ${onDesktop(css`
        ${mh("auto")};
        ${flex("flex-start")};
        ${pt(2)};
        ${pb(1)};
        gap: ${calculateRemSize(2)};
        position: relative;
        flex-grow: 1;
        flex-shrink: 1;
        flex-wrap: wrap;
    `)};
`;

const searchFormWrapperStyle = css`
    ${flex()};
    flex: 1 1;
`;

const changingButtonsWrapper = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.lg}) {
        ${mr(9)};
    }
`;

const searchInputWrapper = css`
    ${display("none")};

    ${onDesktop(css`
        ${flex()};
        ${flexDirection("row")};
        width: 100%;
        flex: 1 1;
    `)}
`;

const actionButtonStyle = css`
    width: 18rem;
    overflow: visible;
`;

const filtersCountBadge = css`
    position: absolute;
    right: -${calculateRemSize(0.5)};
    top: -${calculateRemSize(0.5)};
`;

const drawerContentStyle = css`
    ${flex()};
    ${flexDirection("column")};
    ${p(0)};
    overflow: hidden;
`;

const Wrapper = styled.div`
    &.isInNavigationBar {
        ${pb(0)};
        > div:nth-of-type(2) {
            ${pt(0)};
        }
    }
`;

const filtersButtonsStyle = css`
    ${flex()};
    gap: ${calculateRemSize(2)};
`;

export const ResultWitchTravelTimeWrapper = styled.div`
    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        max-height: 74rem;
    }
`;
import React, {MutableRefObject, useEffect, useRef, useState} from "react";
import {connect, useDispatch, useSelector} from "react-redux";
import {RouteComponentProps, withRouter} from "react-router";
import {css, SerializedStyles, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {bindActionCreators, Dispatch} from "redux";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {Drawer} from "@pg-design/drawer-module";
import {Hidden} from "@pg-design/grid-module";
import {calculateRemSize, display, flex, flexDirection, h100, mh, mr, onDesktop, p, pb, pt, pv} from "@pg-design/helpers-css";
import {FiltersIcon} from "@pg-design/icons-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {FormActions} from "@pg-mono/form";
import {appendQueryString, parseSearch} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {rpFeatureFlagNewUserFlow} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {getOfferListSubFilterParsed} from "../../offer/helpers/friendly_offer_list/get_offer_list_sub_filter_parsed";
import {resetPrivilegedOfferList} from "../../offer/list/actions/fetch_privileged_offer_list";
import {ISelectedRegion} from "../../offer/list/actions/offer_list_selected_data_actions";
import {NotificationVariant, OfferListNotificationsSubscriptionModal} from "../../offer/list/components/OfferListNotificationsSubscriptionModal";
import {OfferListSortMobile} from "../../offer/list/components/OfferListsSortMobile";
import {SearchSortString} from "../../offer/list/constants/SearchSort";
import {useDefaultDistance} from "../../offer/list/hooks/use_default_distance";
import {useOfferListingAbroadView} from "../../offer/list/hooks/use_offer_listing_abroad_view_check";
import {useMapRegionDistrictsAndSurroundings} from "../../offer/map/hooks/use_map_region_districts_and_surroundings";
import {validateSelectedRegionsWithAvailableDistrictsAndSurroundings} from "../../offer/map/utils/districts_and_surroundings/validate_selected_regions_with_available_districts_and_surroundings";
import {debouncedSearchTypingHit} from "../../tracking/algolytics_hits/search_typing_hit";
import {ILatestQuery} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {
    fetchAllSearchLists,
    IFetchAllSearchListsParams,
    resetFetchAllSearchLists,
    SearchTab,
    stopFetchAllSearchLists
} from "../actions/fetch_search_all_action";
import {resetPlaceList} from "../actions/fetch_search_places_action";
import {resetMultiRegionSearchForm, resetSearchForm, searchFormActions} from "../actions/search_form_actions";
import {resetActiveDropdownItem} from "../actions/set_active_dropdown_item";
import {setPreviousDistance} from "../actions/set_travel_time";
import {GuideTourStep} from "../constants/GuideTourStep";
import {searchFormFields} from "../constants/search_form_constants";
import {SEARCH_FORM_FOCUS_HASH} from "../constants/search_form_focus_hash";
import {searchFormWithDrawerHeight} from "../constants/search_form_with_drawer_height";
import {useCountSearchFilters} from "../hooks/use_count_search_filters";
import useFilter from "../hooks/use_filter";
import useMobileFilters from "../hooks/use_mobile_filters";
import {useRegionSearchTabs} from "../hooks/use_region_search_tabs";
import useSearchAndFilterUpdate from "../hooks/use_search_and_filter_update";
import {useSearchForm} from "../hooks/use_search_form";
import {useSearchFormSubmit} from "../hooks/use_search_form_submit";
import {useSearchGuideTour} from "../hooks/use_search_guide_tour";
import {ISearchFiltersFormValues} from "../types/ISearchFiltersFormValues";
import {RegionSearchTabsType} from "../types/RegionSearchTabsType";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {getSearchFiltersByOfferType} from "../utils/get_search_filters_by_offer_type";
import {googlePlacesDivId} from "../utils/search_constants";
import {DesktopBlend} from "./atoms/atoms";
import {ISearchAutocompleteFormValues} from "./search_autocomplete/SearchAutocomplete";
import {SlotOrderType} from "./search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchAutocompleteWithDropdown} from "./search_autocomplete/SearchAutocompleteWithDropdown";
import {SearchAutocompleteWithRegionAdjustments} from "./search_autocomplete/SearchAutocompleteWithRegionAdjustments";
import {ListingChangingButtons} from "./ListingChangingButtons";
import {MobileSearchFilters} from "./MobileSearchFilters";
import {MobileSearchTriggers} from "./MobileSearchTriggers";
import {MobileTopSearchTriggers} from "./MobileTopSearchTriggers";
import {SearchFiltersWithAccordion} from "./SearchFiltersWithAccordion";

interface IStateProps {
    formValues: SearchFormValuesType;
}
// TODO: rebrand
/* eslint-disable @typescript-eslint/no-explicit-any */
interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, any>>;
    resetSearchForm: any;
    resetMultiRegionSearchForm: any;
    resetActiveDropdownItem: typeof resetActiveDropdownItem;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
    stopFetchAllSearchLists: typeof stopFetchAllSearchLists;
    resetFetchAllSearchLists: typeof resetFetchAllSearchLists;
    resetPrivilegedOfferList: typeof resetPrivilegedOfferList;
    setPreviousDistance: typeof setPreviousDistance;
}

interface IOwnProps extends RouteComponentProps<{}> {
    onAfterSubmit?: () => void;
    latestQuery: ILatestQuery;
    selectedRegions?: ISelectedRegion[];
    multiRegionLink: () => string;
    listFriendlyLink: (params: {friendlySlug: string; offerListSubFilterSlug?: string}) => string;
    abroadListFriendlyLink: (params: {friendlySlug: string; country: string; abroadRegionSlug?: string}) => string;
    blendStyles?: SerializedStyles;
    sortActive?: boolean;
    shouldRenderListBackBtn?: boolean;
    blendTop?: string;
    blendHeightDecrease?: string;
    className?: string;
    slotOrder?: SlotOrderType;
    hideListChangingButtons?: boolean;
    isMap?: boolean;
    showTopFilters?: boolean;
    hideBottomPadding?: boolean;
    disableLocationSearch?: boolean;
    autoFocusOnInit?: boolean;
    disableFilterAutoShowcase?: boolean;
    guideTourName?: string;
}

interface IProps extends IOwnProps, IStateProps, IActionsProps {}

const SearchFormWithDrawerC = (props: IProps) => {
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const {
        isLocationSearchOpen: isLocationSearchOpenGlobal,
        setLocationSearchOpenStatus,
        toggleLocationSearch,
        addLocationSearchStatusWatcher
    } = useSearchForm();
    const isLocationSearchOpen = props.disableLocationSearch ? false : isLocationSearchOpenGlobal;

    // TODO: setDesktopTypeBlendActive & setDesktopSearchBlendActive need to be refactored,
    // probably display should depend on isLocationSearchOpen and css media query
    const [isDesktopTypeBlendActive, setDesktopTypeBlendActive] = useState(false);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);
    const viewType = useSelector((state: IRPStore) => state.viewType);
    const searchAutocompleteWrapperRef = useRef<HTMLDivElement>(null);
    const {guideTourCurrentStepId, completeGuideTourStep} = useSearchGuideTour({name: props.guideTourName});

    const {activeRegionSearchTab, regionSearchTabs, regionSearchTabChangeHandler} = useRegionSearchTabs({
        regionQuery: props.formValues?.search?.label,
        onSearchTabChange: (tab: RegionSearchTabsType) => {
            if (tab === RegionSearchTabsType.TRAVEL_TIME) {
                props.setPreviousDistance(props.latestQuery.distance);
            }
        }
    });

    const {data: mapDistrictsAndSurroundsData, isLoading: areMapDistrictsAndSurroundingsLoading} = useMapRegionDistrictsAndSurroundings({
        selectedRegions: props.selectedRegions
    });

    const scrollIntoSearchAutocompleteWrapper = () => {
        if (searchAutocompleteWrapperRef.current) {
            const marginOffset = 70;
            const elementTop = searchAutocompleteWrapperRef.current.getBoundingClientRect().top;
            const offsetTop = document.documentElement.scrollTop;
            const scrollToPosition = elementTop + offsetTop - marginOffset;

            window.scrollTo({
                top: scrollToPosition,
                behavior: "smooth"
            });
        }
    };

    useEffect(() => {
        if (!rpFeatureFlagNewUserFlow) {
            return;
        }

        if (!props.autoFocusOnInit || guideTourCurrentStepId !== GuideTourStep.SELECT_DISTRICTS || areMapDistrictsAndSurroundingsLoading) {
            return;
        }

        // should be opening and scroll into view shortly after the component is mounted
        if (validateSelectedRegionsWithAvailableDistrictsAndSurroundings(props.selectedRegions || [], mapDistrictsAndSurroundsData?.results)) {
            setTimeout(() => {
                setLocationSearchOpenStatus(true);
                addLocationSearchStatusWatcher(() => {
                    completeGuideTourStep();
                }, false);
            }, 2000);
        } else {
            completeGuideTourStep();
        }
    }, [guideTourCurrentStepId, areMapDistrictsAndSurroundingsLoading]);

    const {isMobileFiltersActive, closeMobileFilterModal, toggleMobileFilterModal, prevFilterState} = useMobileFilters({
        ...props,
        delayAutoOpen: rpFeatureFlagNewUserFlow ? guideTourCurrentStepId !== GuideTourStep.SELECT_FILTERS : true,
        onClose: () => {
            if (rpFeatureFlagNewUserFlow) {
                completeGuideTourStep();
            }
        }
    });
    const isAbroadOffersView = useOfferListingAbroadView();

    const [windowHeight, setWindowHeight] = useState(0);
    const dispatch = useDispatch();
    const {isMobile} = useUserDevice();

    useEffect(() => {
        if (!isMobile) {
            setDesktopSearchBlendActive(isLocationSearchOpen);

            if (props.autoFocusOnInit && isLocationSearchOpen) {
                scrollIntoSearchAutocompleteWrapper();
            }
        }
    }, [isMobile, isLocationSearchOpen]);

    // handle initial focus on search
    const focusOnSearch = (ref: MutableRefObject<HTMLElement | null>, callback?: () => void) => {
        if (props.location.hash === SEARCH_FORM_FOCUS_HASH && ref.current?.offsetParent) {
            props.history.replace({hash: ""});
            setLocationSearchOpenStatus(true);
            callback && callback();
        }
    };
    // focus on search
    const onInputMount = (ref: MutableRefObject<HTMLInputElement | null>) => {
        focusOnSearch(ref, () =>
            setTimeout(() => {
                ref.current?.focus();
            }, 0)
        );
    };
    // and mobile focus on search
    const onMobileSearchTriggerMount = (ref: MutableRefObject<HTMLDivElement | null>) => {
        focusOnSearch(ref);
    };

    useEffect(() => {
        const query = props.latestQuery as Record<string, string>;
        (async () => {
            if (props.location.pathname === props.multiRegionLink()) {
                await props.resetMultiRegionSearchForm(query, location.search);
            } else {
                await props.resetSearchForm(query);
            }
        })();
    }, []);

    useEffect(() => {
        setWindowHeight(document.body.clientHeight);
    }, [isDesktopSearchBlendActive]);

    /**
     * Callbacks
     */
    const onAfterTabSwitch = () => {
        props.resetActiveDropdownItem();
    };

    /**
     * Submit logic
     */
    const onSubmit = useSearchFormSubmit({
        latestQuery: props.latestQuery,
        listFriendlyLink: ({friendlySlug, offerListSubFilterSlug}) => {
            if (viewType.current === ViewType.OFFER_LIST_MAP && offerListSubFilterSlug) {
                const subFilterQuery = getOfferListSubFilterParsed(offerListSubFilterSlug);
                return appendQueryString(props.listFriendlyLink({friendlySlug, offerListSubFilterSlug}), subFilterQuery);
            }

            return props.listFriendlyLink({friendlySlug, offerListSubFilterSlug});
        },
        abroadListFriendlyLink: (params) => {
            return props.abroadListFriendlyLink && props.abroadListFriendlyLink(params);
        },
        multiRegionLink: props.multiRegionLink,
        onAfterSubmit: props.onAfterSubmit
    });

    const onChange = (values: Partial<ISearchAutocompleteFormValues>) => {
        if (!!values.search?.label && props.formValues.search.label !== values.search.label) {
            debouncedSearchTypingHit({query: values.search.label});
        }

        props.formActions.update(values);
    };
    const onSearchSubmit = () => {
        onSubmit(props.formValues);
    };

    const {onAfterChange} = useSearchAndFilterUpdate({
        resetPrivilegedOfferList: resetPrivilegedOfferList,
        formValues: props.formValues,
        latestQuery: props.latestQuery,
        onSubmit: (formValues: SearchFormValuesType) => onSubmit(formValues)
    });

    const {isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters} = useFilter({
        formValues: props.formValues,
        closeMobileFilterModal: closeMobileFilterModal,
        onAfterChange: onAfterChange,
        onSubmit: (formValues: SearchFormValuesType) => onSubmit(formValues),
        enableFiltersAutoOpen: rpFeatureFlagNewUserFlow ? guideTourCurrentStepId === GuideTourStep.SELECT_FILTERS : false,
        disableAutoShowcase: props.disableFilterAutoShowcase,
        onClose: () => {
            if (rpFeatureFlagNewUserFlow) {
                completeGuideTourStep();
            }
        }
    });

    /**
     * Handle filters
     */
    const searchFiltersConfig = getSearchFiltersByOfferType(props.formValues.type?.[0]);
    const {totalCount: numberOfSearchFilters, countBySlots} = useCountSearchFilters<ISearchFiltersFormValues>(props.formValues, searchFiltersConfig, {
        afterMountOnly: true
    });

    const discardFiltersModal = () => {
        closeMobileFilterModal();
        if (prevFilterState.current != null) {
            props.formActions.update(prevFilterState.current);
        }
    };

    const getUrl = (isMap: boolean) => {
        const search = parseSearch(props.location.search);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {page, sort, ...validSearch} = search;
        const {friendlySlug} = props.match.params as {friendlySlug: string | undefined};
        if (friendlySlug) {
            return appendQueryString(isMap ? rpAppLink.offer.mapFriendly({friendlySlug}) : rpAppLink.offer.listFriendly({friendlySlug}), validSearch);
        }
        return appendQueryString(isMap ? rpAppLink.offer.multiListMap() : rpAppLink.offer.multiList(), validSearch);
    };

    const onTravelTimeCancel = () => {
        regionSearchTabChangeHandler(RegionSearchTabsType.LOCATION);
        dispatch(resetPlaceList());
    };

    const renderSearchAutocomplete = (onDropdownStatusChange: (isOpen: boolean) => void, isInModal?: boolean) => {
        if (props.disableLocationSearch) {
            return null;
        }

        return isAbroadOffersView ? (
            <SearchAutocompleteWithDropdown
                isDropdownOpen={isLocationSearchOpen}
                onDropdownStatusChange={setLocationSearchOpenStatus}
                formProps={props}
                onAfterChange={onAfterChange}
                onSearchSubmit={onSearchSubmit}
                onAfterTabSwitch={() => null}
                onInputMount={() => null}
                includeDistanceFilter
                isInModal={isInModal}
                placeholder="Zmień lokalizację"
                showArrowOnMobile
            />
        ) : (
            <SearchAutocompleteWithRegionAdjustments
                ref={searchAutocompleteWrapperRef}
                activeRegionSearchTab={activeRegionSearchTab}
                defaultDistanceValue={defaultDistanceValue}
                fetchAllSearchLists={props.fetchAllSearchLists}
                formActions={props.formActions}
                formValues={props.formValues}
                isInModal={isInModal}
                onAfterChange={onAfterChange}
                onAfterTabSwitch={onAfterTabSwitch}
                onChange={onChange}
                isDropdownOpen={isLocationSearchOpen}
                onDropdownStatusChange={onDropdownStatusChange}
                onInputMount={onInputMount}
                onSearchSubmit={onSearchSubmit}
                onTravelTimeCancel={onTravelTimeCancel}
                setDesktopTypeBlendActive={setDesktopTypeBlendActive}
                slotOrder={props.slotOrder}
                regionSearchTabChangeHandler={regionSearchTabChangeHandler}
                regionSearchTabs={regionSearchTabs}
            />
        );
    };

    /**
     * Render
     */
    const defaultDistanceValue = useDefaultDistance(props.formValues.distance || props.latestQuery?.distance);

    const mobileFiltersProps = {
        selectedRegionsCount: props.formValues["search"].regions ? props.formValues["search"].regions.length : 0,
        selectedFiltersCount: numberOfSearchFilters,
        onToggleSearchClick: toggleLocationSearch,
        onToggleFiltersClick: toggleMobileFilterModal,
        onToggleSortClick: props.sortActive ? () => setMobileSortActive((prev) => !prev) : null,
        mapUrl: props.shouldRenderListBackBtn ? getUrl(true) : undefined,
        listUrl: !props.shouldRenderListBackBtn ? getUrl(false) : undefined,
        latestQuery: props.latestQuery,
        onMount: onMobileSearchTriggerMount,
        firstSelectedRegion: props.formValues.search?.regions?.[0]?.name,
        sortActive: props.sortActive
    };

    const onDrawerClose = () => {
        if (props.latestQuery.price_1 !== props.formValues.price.upper) {
            props.formActions.update({price: {lower: "", upper: props.latestQuery.price_1 || ""}});
        }
        closeDrawer();
    };

    return (
        <Wrapper className={props.className}>
            <DesktopBlend
                isActive={isDesktopSearchBlendActive || isDesktopTypeBlendActive}
                pageHeight={`${windowHeight}px`}
                top={props.blendTop || `${searchFormWithDrawerHeight}px`}
                heightDecrease={props.blendHeightDecrease ? props.blendHeightDecrease : `${searchFormWithDrawerHeight}px`}
            />
            {!props.hideListChangingButtons && (
                <Hidden on="desktop" css={pv(1.5)}>
                    <ListingChangingButtons isMap={props.isMap} />
                </Hidden>
            )}

            {!isMobile && (
                <div css={[searchSectionWrapper]}>
                    {!props.hideListChangingButtons && (
                        <Hidden on="mobile" css={changingButtonsWrapper}>
                            <ListingChangingButtons isMap={props.isMap} />
                        </Hidden>
                    )}
                    <div css={searchFormWrapperStyle}>
                        <div css={searchInputWrapper}>
                            <div id={googlePlacesDivId} />
                            {renderSearchAutocomplete(setLocationSearchOpenStatus)}
                        </div>
                    </div>
                    <div css={filtersButtonsStyle}>
                        <Button
                            size="small"
                            variant="filled_primary"
                            iconLeft={FiltersIcon}
                            css={[actionButtonStyle, display("none"), onDesktop(display("inline-flex"))]}
                            onClick={() => setDrawerOpen(true)}
                        >
                            FILTRY
                            {numberOfSearchFilters > 0 ? (
                                <Badge variant="label_danger" css={filtersCountBadge}>
                                    {numberOfSearchFilters}
                                </Badge>
                            ) : null}
                        </Button>
                        {props.formValues.search?.regions && <OfferListNotificationsSubscriptionModal variant={NotificationVariant.ICON} />}
                    </div>
                </div>
            )}
            <Drawer isOpen={isDrawerOpen} onDrawerClose={onDrawerClose} contentStyle={{backgroundColor: getThemeVariable("colors.background.100")}}>
                <Drawer.Header>Filtry</Drawer.Header>
                <Drawer.Content css={[drawerContentStyle, h100]}>
                    <SearchFiltersWithAccordion
                        searchFiltersConfig={searchFiltersConfig}
                        searchFiltersCountBySlots={countBySlots}
                        values={props.formValues}
                        onAfterChange={onAfterChange}
                        onChange={props.formActions.update}
                        onSubmit={onSearchSubmit}
                        onClose={onDrawerClose}
                        onResetForm={resetFilters}
                    />
                </Drawer.Content>
            </Drawer>

            {isMobile && (
                <>
                    <MobileOptionsModal
                        modalHeading="Wyszukiwanie"
                        isOpen={isLocationSearchOpen}
                        onModalClose={() => {
                            setLocationSearchOpenStatus(false);
                        }}
                    >
                        <div id={googlePlacesDivId} />
                        {renderSearchAutocomplete(setLocationSearchOpenStatus, true)}
                    </MobileOptionsModal>
                    <MobileOptionsModal
                        modalHeading="Filtry"
                        isOpen={isMobileFiltersActive}
                        onModalClose={discardFiltersModal}
                        css={[flex(), flexDirection("column")]}
                    >
                        <MobileSearchFilters
                            searchFiltersConfig={searchFiltersConfig}
                            searchFiltersCountBySlots={countBySlots}
                            values={props.formValues}
                            onAfterChange={onAfterChange}
                            onChange={props.formActions.update}
                            onSubmit={onSearchSubmit}
                            onMobileCloseModal={closeMobileFilterModal}
                            onMobileResetForm={resetFilters}
                            isInModal
                        />
                    </MobileOptionsModal>
                    {props.sortActive && (
                        <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={() => setMobileSortActive(false)}>
                            <OfferListSortMobile
                                name="mobile-sort"
                                onSubmit={() => setMobileSortActive(false)}
                                latestQuerySort={props.latestQuery.sort as SearchSortString}
                                onMobileResetForm={resetFilters}
                            />
                        </MobileOptionsModal>
                    )}
                    {props.showTopFilters ? (
                        <MobileTopSearchTriggers
                            searchFieldPropsValue={props.formValues.search}
                            {...mobileFiltersProps}
                            isMap={props.isMap}
                            showLocationSearch
                        />
                    ) : (
                        <MobileSearchTriggers {...mobileFiltersProps} positionFixed hideBottomPadding={props.hideBottomPadding} />
                    )}
                </>
            )}
        </Wrapper>
    );
};

export const SearchFormWithDrawer = withRouter(connect(mapStateToProps, mapActionsToProps)(SearchFormWithDrawerC));

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.search.formValues
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators(
            {
                resetSearchForm,
                resetActiveDropdownItem,
                resetMultiRegionSearchForm,
                fetchAllSearchLists,
                stopFetchAllSearchLists,
                resetFetchAllSearchLists,
                resetPrivilegedOfferList,
                setPreviousDistance
            },
            dispatch
        )
    };
}

const searchSectionWrapper = css`
    ${display("none")};

    ${onDesktop(css`
        ${mh("auto")};
        ${flex("flex-start")};
        ${pt(2)};
        ${pb(1)};
        gap: ${calculateRemSize(2)};
        position: relative;
        flex-grow: 1;
        flex-shrink: 1;
        flex-wrap: wrap;
    `)};
`;

const searchFormWrapperStyle = css`
    ${flex()};
    flex: 1 1;
`;

const changingButtonsWrapper = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.lg}) {
        ${mr(9)};
    }
`;

const searchInputWrapper = css`
    ${display("none")};

    ${onDesktop(css`
        ${flex()};
        ${flexDirection("row")};
        width: 100%;
        flex: 1 1;
    `)}
`;

const actionButtonStyle = css`
    width: 18rem;
    overflow: visible;
`;

const filtersCountBadge = css`
    position: absolute;
    right: -${calculateRemSize(0.5)};
    top: -${calculateRemSize(0.5)};
`;

const drawerContentStyle = css`
    ${flex()};
    ${flexDirection("column")};
    ${p(0)};
    overflow: hidden;
`;

const Wrapper = styled.div`
    &.isInNavigationBar {
        ${pb(0)};
        > div:nth-of-type(2) {
            ${pt(0)};
        }
    }
`;

const filtersButtonsStyle = css`
    ${flex()};
    gap: ${calculateRemSize(2)};
`;

export const ResultWitchTravelTimeWrapper = styled.div`
    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        max-height: 74rem;
    }
`;
